import { AxiosRequestConfig } from 'axios';
import { HttpResponse, ajax } from '../../../../common/ajax';
import { ODataModel } from '../../../../common/types';
import { config } from '../../config';
import { ProductOption } from '../../models/Options';
import {
  ComputedProductPriceModel,
  ProductDetailsBaseModel,
  ProductListModel,
} from '../../models/Product';
import { SubscriptionItemModel } from '../../models/Subscriptions';

interface IProductsService {
  getSubscriptions: (
    queryString?: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ODataModel<SubscriptionItemModel>>>;
  cancelSubscription: (
    orderProductId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<any>>;
  getProductOptions: (
    productId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ProductOption[]>>;
  getProduct: (
    productId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ProductDetailsBaseModel>>;
  calculatePrice: (
    productId: number,
    quantity: number,
    selectedOptions: any,
    cashbackDiscount: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ComputedProductPriceModel>>;
  getProductsForCategory: (
    categoryId: number | undefined,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ProductListModel[]>>;
  getProductsForBrand: (
    brandId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ProductListModel[]>>;
  getProductsForProductType: (
    productTypeId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ProductListModel[]>>;
  getMostPopularProducts: (
    categoryId?: number | undefined,
    numberOfProducts?: number | undefined,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ProductListModel[]>>;
  getProductsByMostPopularBrands: (
    categoryId?: number | undefined,
    numberOfBrands?: number | undefined,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ProductListModel[]>>;
  getTopDiscountedProducts: (
    categoryId?: number | undefined,
    numberOfProducts?: number | undefined,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<ProductListModel[]>>;
  getCashBackLink: (
    productId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<string>>;
  downloadProductDocumentation: (
    filename: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<null>>;
}

export const productsService: IProductsService = {
  getSubscriptions(queryString, axiosConfig) {
    return ajax.get<ODataModel<SubscriptionItemModel>>(
      `${config.benefitApiUrl}/odata/subscriptions?${queryString}`,
      axiosConfig,
    );
  },
  cancelSubscription(orderProductId, axiosConfig) {
    return ajax.post(
      `${config.benefitApiUrl}/subscriptions/cancel`,
      { orderProductId },
      axiosConfig,
    );
  },
  getProductOptions(productId, axiosConfig) {
    return ajax.get<ProductOption[]>(
      `${config.benefitApiUrl}/products/${productId}/options`,
      axiosConfig,
    );
  },
  getProduct(productId, axiosConfig) {
    return ajax.get<ProductDetailsBaseModel>(
      `${config.benefitApiUrl}/products/${productId}`,
      axiosConfig,
    );
  },
  calculatePrice(
    productId: number,
    quantity: number,
    selectedOptions: any,
    cashbackDiscount: number,
    axiosConfig?: AxiosRequestConfig,
  ) {
    return ajax.post<ComputedProductPriceModel>(
      `${config.benefitApiUrl}/products/${productId}/price`,
      { productId, quantity, selectedOptions, cashbackDiscount },
      axiosConfig,
    );
  },
  getProductsForCategory(
    categoryId: number | undefined,
    axiosConfig,
  ): Promise<HttpResponse<ProductListModel[]>> {
    return ajax.get<ProductListModel[]>(
      `${config.benefitApiUrl}/categories/${categoryId}/products`,
      axiosConfig,
    );
  },
  getProductsForBrand(brandId, axiosConfig) {
    return ajax.get<ProductListModel[]>(
      `${config.benefitApiUrl}/brands/${brandId}/products`,
      axiosConfig,
    );
  },
  getProductsForProductType(productTypeId, axiosConfig) {
    return ajax.get<ProductListModel[]>(
      `${config.benefitApiUrl}/product-types/${productTypeId}/products`,
      axiosConfig,
    );
  },
  getMostPopularProducts(
    categoryId?: number | undefined,
    numberOfProducts?: number | undefined,
    axiosConfig?,
  ): Promise<HttpResponse<ProductListModel[]>> {
    return ajax.get<ProductListModel[]>(
      `${config.benefitApiUrl}/products/most-popular?categoryId=${categoryId}&numberOfProducts=${numberOfProducts}`,
      axiosConfig,
    );
  },
  getProductsByMostPopularBrands(
    categoryId?: number | undefined,
    numberOfBrands?: number | undefined,
    axiosConfig?,
  ): Promise<HttpResponse<ProductListModel[]>> {
    return ajax.get<ProductListModel[]>(
      `${config.benefitApiUrl}/products/most-popular-brands?categoryId=${categoryId}&numberOfBrands=${numberOfBrands}`,
      axiosConfig,
    );
  },
  getTopDiscountedProducts(
    categoryId?: number | undefined,
    numberOfProducts?: number | undefined,
    axiosConfig?,
  ): Promise<HttpResponse<ProductListModel[]>> {
    return ajax.get<ProductListModel[]>(
      `${config.benefitApiUrl}/products/top-discounted?categoryId=${categoryId}&numberOfProducts=${numberOfProducts}`,
      axiosConfig,
    );
  },
  getCashBackLink(
    productId: number,
    axiosConfig?: AxiosRequestConfig,
  ): Promise<HttpResponse<string>> {
    return ajax.get<string>(
      `${config.benefitApiUrl}/products/${productId}/cashclub-link`,
      axiosConfig,
    );
  },
  downloadProductDocumentation(
    filename: string,
    axiosConfig?: AxiosRequestConfig,
  ): Promise<HttpResponse<null>> {
    return ajax.downloadFile(
      `${config.benefitApiUrl}/products/download-documentation`,
      filename,
      '.docx',
      axiosConfig,
    );
  },
};
